import { Injectable } from '@angular/core';
import { VaultApiService } from '@services/api/vault-api.service';
import {
  DerivativeDemandGlobalMtmDailyResponse,
  DerivativeMtmDailyResponse,
} from '@src/app/core/interfaces/derivatives';
import { Observable } from 'rxjs';

/**
 * HTTP Service para consulta de MtM Diário de derivativos
 */
@Injectable({ providedIn: 'root' })
export class DerivativeMtmDailyHttpService {
  private readonly baseUrl = 'operations/ndf';

  constructor(private api: VaultApiService) {}

  /**
   * Busca o histórico de MtM diário de uma operação de derivativo
   * GET {baseUrl}/mtm-daily?derivativeOperationId={id}
   *
   * @param derivativeOperationId ID da operação de derivativo
   * @returns Observable com a lista de registros de MtM diário
   */
  getMtmDaily(derivativeOperationId: string): Observable<DerivativeMtmDailyResponse | null> {
    return this.api.get<DerivativeMtmDailyResponse>(
      `${this.baseUrl}/mtm-daily?derivativeOperationId=${derivativeOperationId}`
    );
  }

  getMtmDailyByFilters(filters: {
    derivativeOperationId?: string | null;
    loanOperationId?: string | null;
    loanOperationIds?: string[] | null;
    companyId?: string | null;
    operationStatus?: string | null;
    startDate?: string | null;
    endDate?: string | null;
  }): Observable<DerivativeMtmDailyResponse | null> {
    return this.api.get<DerivativeMtmDailyResponse>(`${this.baseUrl}/mtm-daily`, null, filters);
  }

  /**
   * Versão ENRIQUECIDA da série diária para a tela de Mark to Market (posição): além do MtM,
   * traz sequenciais (derivativo/loan), banco, referências, direção, notional BRL e datas de
   * contratação/vencimento por operação. Endpoint dedicado — o `mtm-daily` base segue enxuto.
   * GET {baseUrl}/mtm-daily/enriched
   */
  getEnrichedMtmDailyByFilters(filters: {
    derivativeOperationId?: string | null;
    loanOperationId?: string | null;
    companyId?: string | null;
    operationStatus?: string | null;
    startDate?: string | null;
    endDate?: string | null;
  }): Observable<DerivativeMtmDailyResponse | null> {
    return this.api.get<DerivativeMtmDailyResponse>(
      `${this.baseUrl}/mtm-daily/enriched`,
      null,
      filters
    );
  }

  /**
   * Gera o cálculo de MtM diário para todos os dias úteis de uma operação de derivativo
   * POST {baseUrl}/mtm-daily/{operationId}/generate
   *
   * @param operationId ID da operação de derivativo
   * @param regenerateAll Se true, remove todos os MTMs diários existentes e recalcula do zero
   * @returns Observable com a resposta da geração
   */
  generateMtmDaily(operationId: string, regenerateAll: boolean = false): Observable<unknown> {
    const queryParam = regenerateAll ? '?regenerateAll=true' : '';
    return this.api.post<unknown>(
      `${this.baseUrl}/mtm-daily/${operationId}/generate${queryParam}`,
      {}
    );
  }

  /**
   * Busca o MtM diário consolidado da demanda considerando apenas operações contratadas
   * GET {baseUrl}/mtm-daily/demand/{demandId}/global
   */
  getGlobalMtmDailyByDemandId(
    demandId: string
  ): Observable<DerivativeDemandGlobalMtmDailyResponse | null> {
    return this.api.get<DerivativeDemandGlobalMtmDailyResponse>(
      `${this.baseUrl}/mtm-daily/demand/${demandId}/global`
    );
  }
}
